import React, { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import Box from '@material-ui/core/Box'
import EventAvailableIcon from 'components/material-symbol/icons/EventAvailable'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import AddToPhotosOutlinedIcon from 'components/material-symbol/icons/AddToPhotos'
import LinkOutlinedIcon from 'components/material-symbol/icons/Link'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import LinearProgress from '@material-ui/core/LinearProgress'
import Switch from '@material-ui/core/Switch'
import TimerIcon from 'components/material-symbol/icons/Timer'
import BookOutlinedIcon from 'components/material-symbol/icons/Book'
import ComputerOutlinedIcon from 'components/material-symbol/icons/Computer'
import ChatBubbleOutlinedIcon from 'components/material-symbol/icons/ChatBubble'
import LaunchOutlinedIcon from 'components/material-symbol/icons/Launch'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { useAuth } from 'lib/auth'
import useStyles, { SettingsFormControlLabel } from './settings.styles'
import {
  SettingsAccountRow,
  SettingsLinkRow,
  SettingsSection,
  SettingsToggleRow,
} from './settings-components'
import Error from 'components/error'
import { PageHeader } from 'components/page-header'
import { usePortal } from 'lib/portal'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import SocialShareDialog from 'components/social-share-dialog'
import { gtmEvent } from 'lib/gtm'
import { useCreateSubscriptionPaymentSession } from 'lib/graphql/mutations/create-subscription-payment-session'
import { COACH_PREFERENCES, UPDATE_COACH_PREF } from './queries'
import { MY_PGA_COM_HOST } from 'env'
import { ReactComponent as AutoRespondersIcon } from 'images/icons/auto-responders.svg'

const ToggleSwitch = ({
  checked,
  enabled,
  mutation,
  eventCategory,
  formAction,
  ariaLabel,
  onCheckedChange,
}) => {
  const [state, setState] = useState(checked)
  const [toggle] = useMutation(mutation)
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    setState(checked)
  }, [checked])

  return (
    <Switch
      checked={state}
      disabled={!enabled}
      inputProps={{ 'aria-label': ariaLabel }}
      onChange={event => {
        toggle({
          variables: { input: { [formAction]: event.target.checked } },
        }).then(({ data: { updateCoachPreferences: op } }) => {
          gtmEvent({
            event: 'formSubmit',
            formCategory: eventCategory,
            formAction: op.coach.preferences[formAction]
              ? `${formAction}-enable`
              : `${formAction}-disable`,
          })
          enqueueSnackbar(op.message, {
            variant: op.success ? 'success' : 'error',
          })
          const nextState =
            op.coach.preferences[
              formAction === 'bookNow' ? 'bookNowEnabled' : formAction
            ]
          setState(nextState)
          onCheckedChange?.(nextState)
        })
      }}
    />
  )
}

const SettingsPage = () => {
  const { user } = useAuth()
  const [target] = usePortal()
  const styles = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const successUrl = `${MY_PGA_COM_HOST}/pga-coach/settings`
  const [
    callCreateSubscriptionPaymentSession,
    { loading: loadingPaymentSession },
  ] = useCreateSubscriptionPaymentSession()

  const handleUpgradeClick = async () => {
    try {
      const { data } = await callCreateSubscriptionPaymentSession({
        variables: { successUrl },
      })
      const {
        url,
        success,
        message,
      } = data?.createPgaCoachSubscriptionPaymentSession
      if (!success) {
        return snackbarError(message)
      }
      if (success && url) {
        window.location.href = url
      }
    } catch (e) {
      snackbarError(e?.message)
    }
  }

  const { data, loading, error } = useQuery(COACH_PREFERENCES)
  const [bookNowOverride, setBookNowOverride] = useState(null)
  const [acceptsLeadsOverride, setAcceptsLeadsOverride] = useState(null)

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  const {
    freeTierLimitsReached,
    preferences,
    bookingUrl,
    googleCalendarConnected,
  } = data?.currentUser?.attributes || {}
  const enableContactButton = !freeTierLimitsReached?.includes('LEADS')
  const bookNowEnabled = bookNowOverride ?? !!preferences?.bookNowEnabled
  const acceptsLeadsEnabled =
    acceptsLeadsOverride ?? !!preferences?.acceptsLeads
  const resolvedBookingUrl = bookingUrl || user?.coach?.bookingUrl
  const coachName =
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    'Account'

  return (
    <>
      <PageHeader title="Settings" actions={target} />
      <Box className={styles.contentContainer}>
        <Box className={styles.card}>
          <SettingsAccountRow name={coachName} classes={styles} />
        </Box>

        <SettingsSection eyebrow="Leads" classes={styles}>
          <SettingsLinkRow
            to="settings/leads-auto-reply"
            icon={
              <AutoRespondersIcon
                style={{
                  fill: 'currentColor',
                  width: 16,
                  height: 16,
                }}
              />
            }
            title="Leads auto-reply"
            description="Send an automatic response to new lead inquiries."
            badge={preferences?.leadAutoReplyEnabled ? 'On' : 'Off'}
            classes={styles}
          />
        </SettingsSection>

        <SettingsSection eyebrow="Schedule" classes={styles}>
          <SettingsLinkRow
            to="settings/availability"
            icon={<EventAvailableIcon />}
            title="Availability settings"
            description="Set weekly hours, one-time openings, and blocked times."
            classes={styles}
          />
          <SettingsLinkRow
            to="settings/booking-limits"
            icon={<TimerIcon />}
            title="Booking & cancellation rules"
            description="Control how far in advance students can book or cancel."
            classes={styles}
          />
          <SettingsLinkRow
            to="settings/google-calendar"
            icon={<CalendarTodayIcon />}
            title="Google calendar"
            description="Sync blocked times and push events to your Google Calendar."
            badge={googleCalendarConnected ? undefined : 'Not Connected'}
            classes={styles}
          />
        </SettingsSection>

        <SettingsSection eyebrow="Coach profile" classes={styles}>
          <SettingsLinkRow
            to="settings/facilities"
            icon={<RoomOutlinedIcon />}
            title="Manage facilities"
            description="Update the locations shown on your profile and used in searches."
            classes={styles}
          />
          <SettingsLinkRow
            to="settings/booking-url"
            icon={<BookOutlinedIcon />}
            title="Book Now preferences"
            description="Choose how you collect and manage program registrations."
            classes={styles}
          />
          <SettingsToggleRow
            icon={<ComputerOutlinedIcon />}
            title='Show "Book Now" button on profile'
            description="Golfers can book directly from your profile when you have active offerings."
            badge={bookNowEnabled ? 'On' : 'Off'}
            action={
              <SettingsFormControlLabel
                label=" "
                control={
                  <ToggleSwitch
                    checked={bookNowEnabled}
                    enabled={true}
                    mutation={UPDATE_COACH_PREF}
                    eventCategory="book-now"
                    formAction="bookNow"
                    ariaLabel='Show "Book Now" button on profile'
                    onCheckedChange={setBookNowOverride}
                  />
                }
              />
            }
            classes={styles}
          />
          <SettingsToggleRow
            icon={<ChatBubbleOutlinedIcon />}
            title='Show "Contact" button on profile'
            description="Golfers can submit leads through your profile contact form."
            badge={enableContactButton && acceptsLeadsEnabled ? 'On' : 'Off'}
            action={
              <SettingsFormControlLabel
                label=" "
                control={
                  <ToggleSwitch
                    checked={enableContactButton && acceptsLeadsEnabled}
                    enabled={enableContactButton}
                    mutation={UPDATE_COACH_PREF}
                    eventCategory="accept-leads"
                    formAction="acceptsLeads"
                    ariaLabel='Show "Contact" button on profile'
                    onCheckedChange={setAcceptsLeadsOverride}
                  />
                }
              />
            }
            classes={styles}
          />
          {!enableContactButton && !loadingPaymentSession ? (
            <Box className={styles.tipBannerWrapper}>
              <Box className={styles.tipBanner}>
                <Box className={styles.tipBannerCopy}>
                  <Typography component="span" className={styles.tipBannerText}>
                    Upgrade to accept more than 2 leads on your profile.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  className={styles.tipBannerButton}
                  onClick={handleUpgradeClick}
                  endIcon={
                    <LaunchOutlinedIcon fontSize="small" opticalSize={18} />
                  }
                >
                  Upgrade
                </Button>
              </Box>
            </Box>
          ) : null}
          {loadingPaymentSession ? <LinearProgress /> : null}
        </SettingsSection>

        <SettingsSection eyebrow="Your programs" classes={styles}>
          <SettingsLinkRow
            to="bookings/offerings"
            icon={<AddToPhotosOutlinedIcon />}
            title="Manage offerings"
            description="Set up lessons, clinics, and other offerings."
            classes={styles}
          />
          <SocialShareDialog
            trigger={
              <SettingsLinkRow
                icon={<LinkOutlinedIcon />}
                title="Share booking link"
                description="Copy your link to share via text, email, or social media."
                classes={styles}
              />
            }
            shareUrl={resolvedBookingUrl}
            shareTitle="My Offerings"
            showBookingUrl={true}
            att="booking-link"
          />
        </SettingsSection>
      </Box>
    </>
  )
}

export default SettingsPage
